import { useQueryClient } from 'react-query';
import { powerProfileModes } from 'greener-ui-library';
import useAxiosMutation from 'hooks/useAxiosMutation';
import { BlueprintFormData } from 'pages/Blueprints/NewBlueprint/types';
import { buildCalculations } from 'pages/Blueprints/NewBlueprint/utils/buildCalculations';
import { daysToIndices } from 'pages/Blueprints/NewBlueprint/utils/sortDays';
import { projectTypeStringToId } from 'pages/Blueprints/components/utils';
import { BLUEPRINTS_BASE_KEY } from 'services/queryKeys';
import { BlueprintFromApi } from 'types';

export const useCreateBlueprint = () => {
  const queryClient = useQueryClient();

  const { mutateAsync, isLoading, isError, error } = useAxiosMutation<BlueprintFromApi>({
    onSuccess: () => {
      // Invalidate blueprints list to refresh data
      queryClient.invalidateQueries(BLUEPRINTS_BASE_KEY);
    },
  });

  const createBlueprint = async (formData: BlueprintFormData) => {
    // Create FormData for file upload
    const formDataToSend = new FormData();

    const calculations = buildCalculations(formData);

    // Create blueprint_data as JSON string
    const blueprintData = {
      name: formData.blueprintName,
      ts_start: formData.startDate instanceof Date ? formData.startDate.toISOString() : new Date().toISOString(),
      ts_created: new Date().toISOString(),
      ts_end: formData.endDate instanceof Date ? formData.endDate.toISOString() : null,
      client_name: formData.clientName,
      partner_organisation_id: formData.partnerOrganisationId ?? null,
      project_type_id: formData.applicationType ? projectTypeStringToId(formData.applicationType) : -1,
      hybrid_reasons: [
        ...(formData.hybridReasons ?? []),
        ...(formData.hybridReasonsCustom?.trim() ? [formData.hybridReasonsCustom.trim()] : []),
      ],
      calculations: calculations,
      power_profile_create_mode: formData.powerProfileMode,
      ...(formData.powerProfileMode === powerProfileModes.manual && {
        power_profile_generation_data: {
          active_days: daysToIndices(formData.workingDays ?? []),
          active_period_peak_power_kw: formData.peakPowerKw,
          active_period_average_power_kw: formData.averagePowerKw,
          active_period_start_time: formData.workingHoursStart,
          active_period_end_time: formData.workingHoursEnd,
          peaks_per_active_period: formData.peaksPerDay,
          peaks_duration_seconds: formData.peaksDuration,
          idle_period_average_power_kw: formData.restDayAveragePowerKw,
        },
      }),
    };

    formDataToSend.append('blueprint_data', JSON.stringify(blueprintData));

    if (formData.powerProfileMode === powerProfileModes.file) {
      // Raw power profile - original data
      // Always use text/csv MIME type - backend processes all file types and returns CSV
      if (formData.rawPowerProfileCsv) {
        const rawFileName = 'raw-power-profile.csv';
        const rawMimeType = 'text/csv';
        const rawCsvBlob = new Blob([formData.rawPowerProfileCsv], { type: rawMimeType });
        formDataToSend.append('raw_power_profile', rawCsvBlob, rawFileName);
      }

      // Edited power profile - ALWAYS required by backend
      // If user didn't edit the chart, send the raw data as edited
      // Always use text/csv MIME type - backend processes all file types and returns CSV
      const editedCsvData = formData.editedPowerProfileCsv || formData.rawPowerProfileCsv;
      if (editedCsvData) {
        const editedFileName = 'edited-power-profile.csv';
        const editedMimeType = 'text/csv';
        const editedCsvBlob = new Blob([editedCsvData], { type: editedMimeType });
        formDataToSend.append('edited_power_profile', editedCsvBlob, editedFileName);
      }
    }

    return mutateAsync({
      method: 'POST',
      path: '/blueprints/',
      body: formDataToSend,
    });
  };

  return {
    createBlueprint,
    isLoading,
    isError,
    error,
  };
};
